/**
 * Eine Zeile in der SupplierInvoicesUploadModal-Multi-File-Table.
 * Required: supplier, amount, issuedOn, category.
 */
import { Input, Select } from '@/shared/components';
import { FileBadge, RowStatusOrRemove } from './AkteUploadAffordances';
import { EXPENSE_CATEGORY_LABELS as CATEGORY_LABELS } from '../expenseCategoryLabels';
import { EXPENSE_CATEGORIES, type SupplierInvoiceRow } from './supplierInvoicesUploadModel';

interface Props {
  row: SupplierInvoiceRow;
  defaultCurrency: string;
  onChange: (patch: Partial<SupplierInvoiceRow>) => void;
  onRemove: () => void;
  disabled: boolean;
}

export function SupplierInvoicesUploadRow({
  row,
  defaultCurrency,
  onChange,
  onRemove,
  disabled,
}: Props) {
  return (
    <tr className="border-line/40 border-b align-top last:border-b-0">
      <td className="py-2 pr-3">
        <div className="flex items-start gap-2">
          <FileBadge file={row.file} />
          <div className="min-w-0">
            <div className="text-ink truncate text-xs font-medium">{row.file.name}</div>
            <div className="text-ink-faint mt-0.5 text-[10px]">
              {(row.file.size / 1024).toFixed(0)} KB
              {row.error && <span className="text-sealwax ml-2">{row.error}</span>}
            </div>
          </div>
        </div>
      </td>
      <td className="py-2 pr-2">
        <Input
          value={row.supplier}
          onChange={(e) => onChange({ supplier: e.target.value })}
          placeholder="Vodafone"
          disabled={disabled}
          className="py-1.5! text-xs"
        />
      </td>
      <td className="py-2 pr-2">
        <Select
          value={row.category}
          onChange={(e) => onChange({ category: e.target.value as SupplierInvoiceRow['category'] })}
          disabled={disabled}
          className="py-1.5! text-xs"
        >
          <option value="">—</option>
          {EXPENSE_CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {CATEGORY_LABELS[c] ?? c}
            </option>
          ))}
        </Select>
      </td>
      <td className="py-2 pr-2">
        <div className="flex gap-1">
          <Input
            type="number"
            step="0.01"
            value={row.amount}
            onChange={(e) => onChange({ amount: e.target.value })}
            placeholder="120,00"
            disabled={disabled}
            className="py-1.5! text-xs"
          />
          <Select
            value={row.currency || defaultCurrency}
            onChange={(e) => onChange({ currency: e.target.value })}
            disabled={disabled}
            className="w-20! py-1.5! text-xs"
          >
            <option value="EUR">EUR</option>
            <option value="USD">USD</option>
            <option value="CHF">CHF</option>
          </Select>
        </div>
      </td>
      <td className="py-2 pr-2">
        <Input
          type="date"
          value={row.issuedOn}
          onChange={(e) => onChange({ issuedOn: e.target.value })}
          disabled={disabled}
          className="py-1.5! text-xs"
        />
      </td>
      <td className="py-2 pr-2">
        <Input
          type="date"
          value={row.dueOn}
          onChange={(e) => onChange({ dueOn: e.target.value })}
          disabled={disabled}
          className="py-1.5! text-xs"
        />
      </td>
      <td className="w-12 py-2 text-center">
        <RowStatusOrRemove status={row.status} onRemove={onRemove} disabled={disabled} />
      </td>
    </tr>
  );
}
